import * as martinez from 'martinez-polygon-clipping';

// Convert {x,y} points -> Martinez polygon ring with proper CCW winding and explicit close.
export const toMartinezPolygon = (points) => {
  let ring = points.map((p) => [p.x, p.y]);

  // Winding-order check (must be counter-clockwise for martinez)
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length;
    area += ring[i][0] * ring[j][1];
    area -= ring[j][0] * ring[i][1];
  }
  if (area > 0) ring = ring.reverse();

  // Close the ring if not already closed
  if (
    ring[0][0] !== ring[ring.length - 1][0] ||
    ring[0][1] !== ring[ring.length - 1][1]
  ) {
    ring.push(ring[0]);
  }
  return [ring];
};

// Build a wedge polygon (sector) from `center` with given outer radius and angle range.
export const buildSectorWedge = (
  center,
  radiusOuter,
  startAngleDeg,
  endAngleDeg,
  steps = 64
) => {
  const toRad = (deg) => (deg * Math.PI) / 180;

  let start = toRad(startAngleDeg);
  let end = toRad(endAngleDeg);
  if (end <= start) end += Math.PI * 2;

  const points = [];
  points.push([center.x, center.y]);

  const step = (end - start) / steps;
  for (let i = 0; i <= steps; i++) {
    const angle = start + i * step;
    points.push([
      center.x + radiusOuter * Math.cos(angle),
      center.y + radiusOuter * Math.sin(angle),
    ]);
  }
  points.push([center.x, center.y]);

  return [points];
};

// Intersect a sector wedge with a boundary polygon and return all valid rings as {x,y} arrays.
export const clipSectorWithBoundary = (
  center,
  radiusOuter,
  startAngleDeg,
  endAngleDeg,
  boundaryPoints
) => {
  try {
    if (!boundaryPoints || boundaryPoints.length < 3) return [];

    const xs = boundaryPoints.map((p) => p.x);
    const ys = boundaryPoints.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);

    const corners = [
      { x: minX, y: minY }, { x: maxX, y: minY },
      { x: maxX, y: maxY }, { x: minX, y: maxY },
    ];
    const dynamicRadius = Math.max(
      ...corners.map((c) => Math.hypot(c.x - center.x, c.y - center.y))
    ) * 1.2;

    const sectorPoly = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
    const boundaryPoly = toMartinezPolygon(boundaryPoints);

    const result = martinez.intersection(sectorPoly, boundaryPoly);
    if (!result || result.length === 0) return [];

    const allRings = [];
    for (const polygon of result) {
      for (const ring of polygon) {
        if (!ring || ring.length < 3) continue;
        const cleaned =
          ring[0][0] === ring[ring.length - 1][0] &&
          ring[0][1] === ring[ring.length - 1][1]
            ? ring.slice(0, -1)
            : ring;
        if (cleaned.length >= 3) {
          allRings.push(cleaned.map(([x, y]) => ({ x, y })));
        }
      }
    }
    return allRings;
  } catch (e) {
    return [];
  }
};

// Approximate sector area using shoelace over points-in-sector + sector boundary.
export const calculateSectorArea = (center, startAngle, endAngle, polygonPoints) => {
  const startRad = (startAngle * Math.PI) / 180;
  const endRad = (endAngle * Math.PI) / 180;

  const pointsInSector = [];

  for (const point of polygonPoints) {
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    let angle = Math.atan2(dy, dx);

    if (angle < 0) angle += 2 * Math.PI;
    if (startRad < 0 && endRad > 0) {
      if (angle > startRad + 2 * Math.PI || angle < endRad) {
        pointsInSector.push(point);
      }
    } else if (angle >= startRad && angle <= endRad) {
      pointsInSector.push(point);
    }
  }

  const getBoundaryPoint = (angle) => {
    const radius = 5000;
    return {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  };

  const startPoint = getBoundaryPoint(startRad);
  const endPoint = getBoundaryPoint(endRad);

  const sectorPolygon = [center, startPoint, ...pointsInSector, endPoint, center];

  let area = 0;
  const n = sectorPolygon.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += sectorPolygon[i].x * sectorPolygon[j].y;
    area -= sectorPolygon[j].x * sectorPolygon[i].y;
  }
  return Math.abs(area) / 2;
};
